import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import Toggle from "../../components/ui/toggle";
import styles from "../../styles/AccountModals.module.css";
import { BASE_URL } from "../../utils/constants";
import Spinner from "../../components/spinner";
import { handleApiError } from "../../utils/handleApiError";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import Button from "../../components/button-v1";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import AlertDialog from "../../components/alert-dialogue";
import useRoles from "../../hooks/useRoles";
import Input from "../../components/ui/input";

// Single source of truth — use ROLE_CAPABILITIES instead of local ROLE_CONFIG
import { ROLE_CAPABILITIES } from "../../config/roleCapabilities";
import { canAccessFeature } from "../../utils/authorization";
import { getUserIdentity } from "../../utils/authorization";
import DefaultPreferencesFields from "../../components/default-preferences/DefaultPreferencesFields";
import useDefaultPreferences from "../../hooks/useDefaultPreferences";

// Helper to build config key from role + userType + serviceType + position
const roleToConfigKey = (role, userType, serviceType = "", position = "") => {
  if (!role) return "";
  const normalizedRole = role.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  if (userType === "toca") return `toca_${normalizedRole}`;
  const normalizedServiceType = (serviceType || "").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  const normalizedPosition = (position || "").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  return `non_toca_${normalizedServiceType}_${normalizedRole}_${normalizedPosition}`;
};

// Maps each functional role to its allowed positions
const ROLE_POSITION_MAP = {
  "Care Manager": ["Medical Assistant", "Nurse"],
  "Account Admin": ["Medical Assistant", "Nurse", "Medical Biller"],
  "Super User": ["Physician", "Nurse", "Medical Biller"],
};

export const AddStaffModal = ({ isOpen, onClose, comp_renderer, current_render }) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // const [accountType, setAccountType] = useState("toca");
  const isTocaUser = getUserIdentity()?.isToca ?? false;
  const [accountType, setAccountType] = useState(isTocaUser ? "toca" : "non_toca");
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", password: "", phone: "",
  });

  // TOCA state
  const [selectedFunctionalRole, setSelectedFunctionalRole] = useState("");
  const [chatbotAccess, setChatbotAccess] = useState(false);
  const [myTasksAccess, setMyTasksAccess] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [expandedTenants, setExpandedTenants] = useState(new Set());
  const [patientLimit, setPatientLimit] = useState("");

  // NON_TOCA state
  const [serviceType, setServiceType] = useState("");
  const [selectedNonTocaFunctionalRole, setSelectedNonTocaFunctionalRole] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedNonTocaLocations, setSelectedNonTocaLocations] = useState([]);

  // API-level access ceiling — if API returns false, we can never pass true
  const [apiChatbotAccess, setApiChatbotAccess] = useState(true);
  const [apiMyTasksAccess, setApiMyTasksAccess] = useState(true);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getRoleConfig = useCallback((role, userType, svcType, position = "") => {
    const key = roleToConfigKey(role, userType, svcType, position);
    const capabilities = ROLE_CAPABILITIES[key];
    if (!capabilities) {
      return {
        copilot: { editable: true, defaultValue: false },
        my_tasks: { editable: true, defaultValue: false },
      };
    }
    return {
      copilot: {
        editable: capabilities.copilot.editable,
        defaultValue: capabilities.copilot.enabled,
      },
      my_tasks: {
        editable: capabilities.my_tasks.editable,
        defaultValue: capabilities.my_tasks.enabled,
      },
    };
  }, []);

  const getPositionName = useCallback((positionId, positions) => {
    return positions?.find(p => String(p.id) === String(positionId))?.name || "";
  }, []);

  const getRoleLabel = (role) =>
    role?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") || "";

  // ─── Helper: extract role value from role object or string ───────────────────
  const extractRoleValue = (role) =>
    typeof role === "string" ? role : role.value || role.name || role;

  const extractRoleLabel = (role) =>
    typeof role === "string" ? getRoleLabel(role) : role.label || role.display_name || getRoleLabel(extractRoleValue(role));

  // ─── Helper: auto-select all NON-TOCA locations ──────────────────────────────
  const autoSelectAllLocations = useCallback((locations) => {
    const allLocationIds = (locations || []).map(l => l.id);
    setSelectedNonTocaLocations(allLocationIds);
  }, []);

  // ─── Helper: auto-select first role + position for NON-TOCA ─────────────────
  const autoSelectNonTocaRoleAndPosition = useCallback((functionalRoles, positions, currentServiceType, apiChatbot, apiMyTasks) => {
    if (!functionalRoles || functionalRoles.length === 0) return;

    const firstRole = extractRoleValue(functionalRoles[0]);
    setSelectedNonTocaFunctionalRole(firstRole);

    const filteredPositions = (positions || []).filter(p =>
      (ROLE_POSITION_MAP[firstRole] || []).includes(p.name)
    );

    if (filteredPositions.length > 0) {
      const firstPosition = filteredPositions[0];
      setSelectedPosition(firstPosition.id);
      const cfg = getRoleConfig(firstRole, "non_toca", currentServiceType, firstPosition.name);
      setChatbotAccess(apiChatbot ? cfg.copilot.defaultValue : false);
      setMyTasksAccess(apiMyTasks ? cfg.my_tasks.defaultValue : false);
    } else {
      setSelectedPosition("");
      setChatbotAccess(false);
      setMyTasksAccess(false);
    }
  }, [getRoleConfig]);

  // ─── Reset ───────────────────────────────────────────────────────────────────

  const resetForm = useCallback((newAccountType = null) => {
    const type = newAccountType !== null ? newAccountType : accountType;
    setFormData({ first_name: "", last_name: "", email: "", password: "", phone: "" });
    if (type === "toca") {
      setSelectedFunctionalRole("");
      setSelectedTenants([]);
      setExpandedTenants(new Set());
      setPatientLimit("");
    } else {
      setServiceType("");
      setSelectedNonTocaFunctionalRole("");
      setSelectedPosition("");
      setSelectedNonTocaLocations([]);
    }
    setChatbotAccess(false);
    setMyTasksAccess(false);
    setErrors({});
  }, [accountType]);

  const handleClose = useCallback(() => { resetForm(); onClose(); }, [resetForm, onClose]);

  // ─── Outside click ───────────────────────────────────────────────────────────

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) handleClose();
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, handleClose]);

  // ─── Fetch dropdown data ─────────────────────────────────────────────────────

  // ─── Dropdown data via useQuery (staleTime = 5min, no refetch on tab switch) ──
  const account_id = sessionStorage.getItem("acc_id");
  const dropdownQuery = useQuery(
    ["onboarding-dropdowns", accountType],
    async () => {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(
        `${BASE_URL}/accounts/${account_id}/onboarding-dropdowns/?user_type=${accountType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    {
      enabled: isOpen,
      staleTime: 1000 * 60 * 5, // 5 minutes — switching toca ↔ non_toca won't refetch if fresh
      onError: (err) => {
        handleApiError(err);
      },
    }
  );

  const dropdownData = dropdownQuery.data ?? null;
  const modalLoading = dropdownQuery.isLoading;

  // ─── Sync dropdown data into local state when it loads/changes ───────────────
  const lastSyncedTypeRef = useRef(null);

  useEffect(() => {
    if (!dropdownData || !isOpen) return;
    // Only run sync once per accountType load to avoid overwriting user selections
    if (lastSyncedTypeRef.current === accountType) return;
    lastSyncedTypeRef.current = accountType;

    const apiChatbot = dropdownData.chatbot_access !== false;
    const apiMyTasks = dropdownData.my_tasks_access !== false;
    setApiChatbotAccess(apiChatbot);
    setApiMyTasksAccess(apiMyTasks);

    if (accountType === "toca") {
      const functionalRoles = dropdownData.functional_roles || [];
      if (functionalRoles.length > 0) {
        const firstRole = extractRoleValue(functionalRoles[0]);
        setSelectedFunctionalRole(firstRole);
        const cfg = getRoleConfig(firstRole, "toca", "", "");
        setChatbotAccess(apiChatbot ? cfg.copilot.defaultValue : false);
        setMyTasksAccess(apiMyTasks ? cfg.my_tasks.defaultValue : false);
      }
      const tenants = dropdownData.tenants || [];
      if (tenants.length > 0) {
        const allTenants = tenants.map(tenant => ({
          tenant_id: tenant.tenant_id,
          location_ids: tenant.locations?.map(l => l.id) || [],
        }));
        setSelectedTenants(allTenants);
        setExpandedTenants(new Set(tenants.map(t => t.tenant_id)));
      }
    } else {
      const serviceTypes = dropdownData.service_types || [];
      const functionalRoles = dropdownData.functional_roles || [];
      const positions = dropdownData.positions || [];
      const locations = dropdownData.locations || [];
      if (serviceTypes.length > 0) {
        const firstServiceType = serviceTypes[0];
        setServiceType(firstServiceType);
        autoSelectNonTocaRoleAndPosition(functionalRoles, positions, firstServiceType, apiChatbot, apiMyTasks);
      }
      autoSelectAllLocations(locations);
    }
  }, [dropdownData, isOpen, accountType, getRoleConfig, autoSelectNonTocaRoleAndPosition, autoSelectAllLocations]);

  // Reset sync ref when modal closes or accountType changes
  useEffect(() => {
    if (!isOpen) lastSyncedTypeRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    lastSyncedTypeRef.current = null;
  }, [accountType]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: "" }));
  };

  const handleAccountTypeChange = useCallback((type) => {
    if (type !== accountType) {
      // Only reset role/access fields, NOT basic info (name, email, phone, password)
      if (type === "toca") {
        setServiceType("");
        setSelectedNonTocaFunctionalRole("");
        setSelectedPosition("");
        setSelectedNonTocaLocations([]);
      } else {
        setSelectedFunctionalRole("");
        setSelectedTenants([]);
        setExpandedTenants(new Set());
        setPatientLimit("");
      }
      setChatbotAccess(false);
      setMyTasksAccess(false);
      setErrors({});
      setAccountType(type);
    }
  }, [accountType]);

  const handleTocaRoleChange = useCallback((role) => {
    setSelectedFunctionalRole(role);
    setErrors(p => ({ ...p, functionalRole: "" }));
    const cfg = getRoleConfig(role, "toca", "", "");
    setChatbotAccess(apiChatbotAccess ? cfg.copilot.defaultValue : false);
    setMyTasksAccess(apiMyTasksAccess ? cfg.my_tasks.defaultValue : false);
    // Reset patient limit when role changes
    setPatientLimit("");
  }, [getRoleConfig, apiChatbotAccess, apiMyTasksAccess]);

  // ── NON-TOCA: When role changes, auto-select first valid position ─────────────
  const handleNonTocaRoleChange = useCallback((role) => {
    setSelectedNonTocaFunctionalRole(role);
    setErrors(p => ({ ...p, functionalRole: "", position: "" }));

    const positions = dropdownData?.positions || [];
    const filteredPositions = positions.filter(p =>
      (ROLE_POSITION_MAP[role] || []).includes(p.name)
    );

    if (filteredPositions.length > 0) {
      const firstPosition = filteredPositions[0];
      setSelectedPosition(firstPosition.id);
      const cfg = getRoleConfig(role, "non_toca", serviceType, firstPosition.name);
      setChatbotAccess(apiChatbotAccess ? cfg.copilot.defaultValue : false);
      setMyTasksAccess(apiMyTasksAccess ? cfg.my_tasks.defaultValue : false);
    } else {
      setSelectedPosition("");
      setChatbotAccess(false);
      setMyTasksAccess(false);
    }
  }, [getRoleConfig, dropdownData, serviceType, apiChatbotAccess, apiMyTasksAccess]);

  const handlePositionChange = useCallback((positionId) => {
    setSelectedPosition(positionId);
    setErrors(p => ({ ...p, position: "" }));
    if (selectedNonTocaFunctionalRole) {
      const positionName = getPositionName(positionId, dropdownData?.positions);
      const cfg = getRoleConfig(selectedNonTocaFunctionalRole, "non_toca", serviceType, positionName);
      setChatbotAccess(apiChatbotAccess ? cfg.copilot.defaultValue : false);
      setMyTasksAccess(apiMyTasksAccess ? cfg.my_tasks.defaultValue : false);
    }
  }, [getRoleConfig, getPositionName, selectedNonTocaFunctionalRole, dropdownData, serviceType, apiChatbotAccess, apiMyTasksAccess]);

  // ── NON-TOCA: When service type changes, auto-select first role + position ────
  const handleServiceTypeChange = useCallback((type) => {
    setServiceType(type);
    setErrors(p => ({ ...p, serviceType: "", functionalRole: "", position: "" }));

    const functionalRoles = dropdownData?.functional_roles || [];
    const positions = dropdownData?.positions || [];
    autoSelectNonTocaRoleAndPosition(functionalRoles, positions, type, apiChatbotAccess, apiMyTasksAccess);
  }, [dropdownData, apiChatbotAccess, apiMyTasksAccess, autoSelectNonTocaRoleAndPosition]);

  // ── TOCA: At least 1 tenant must always be selected ─────────────────────────
  const handleTenantToggle = useCallback((tenant) => {
    setErrors(p => ({ ...p, tenants: "" }));
    const idx = selectedTenants.findIndex(t => t.tenant_id === tenant.tenant_id);
    if (idx >= 0) {
      // Block deselection if it's the last selected tenant
      if (selectedTenants.length === 1) {
        toast.error("At least one organization must be selected.");
        return;
      }
      setSelectedTenants(p => p.filter(t => t.tenant_id !== tenant.tenant_id));
      setExpandedTenants(prev => {
        const newSet = new Set(prev);
        newSet.delete(tenant.tenant_id);
        return newSet;
      });
    } else {
      // Add tenant with all locations pre-selected
      setSelectedTenants(p => [...p, {
        tenant_id: tenant.tenant_id,
        location_ids: tenant.locations?.map(l => l.id) || [],
      }]);
      setExpandedTenants(prev => new Set(prev).add(tenant.tenant_id));
    }
  }, [selectedTenants]);

  // ── TOCA: At least 1 location must remain selected per tenant ────────────────
  const handleLocationToggle = useCallback((tenantId, locationId) => {
    setSelectedTenants(p => p.map(t => {
      if (t.tenant_id === tenantId) {
        // Block deselection if this is the last location for this tenant
        if (t.location_ids.includes(locationId) && t.location_ids.length === 1) {
          toast.error("At least one location must be selected per organization.");
          return t; // return unchanged
        }
        const newIds = t.location_ids.includes(locationId)
          ? t.location_ids.filter(id => id !== locationId)
          : [...t.location_ids, locationId];
        return { ...t, location_ids: newIds };
      }
      return t;
    }));
    setErrors(p => ({ ...p, tenants: "" }));
  }, []);

  const handleNonTocaLocationToggle = useCallback((locationId) => {
    setSelectedNonTocaLocations(p => {
      if (p.includes(locationId)) {
        if (p.length === 1) {
          toast.error("At least one location must be selected.");
          return p;
        }
        return p.filter(id => id !== locationId);
      }
      return [...p, locationId];
    });
    setErrors(p => ({ ...p, locations: "" }));
  }, []);

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const addStaff = async (data) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const account_id = sessionStorage.getItem("acc_id");
      await axios.post(`${BASE_URL}/accounts/${account_id}/account/staff-onboard/`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoading(false);
      resetForm();
      onClose();
      toast.success("Staff added successfully!");
      comp_renderer(p => !p);
    } catch (err) {
      setLoading(false);
      handleApiError(err);
    }
  };

  const validateForm = useCallback(() => {
    const e = {};
    if (!formData.first_name?.trim()) e.first_name = "First Name is required.";
    if (!formData.last_name?.trim()) e.last_name = "Last Name is required.";
    if (!formData.email?.trim()) e.email = "Email is required.";
    if (!formData.password?.trim()) e.password = "Password is required.";
    if (!formData.phone?.trim()) e.phone = "Phone is required.";
    if (accountType === "toca") {
      if (!selectedFunctionalRole) e.functionalRole = "Functional role is required.";
      if (selectedFunctionalRole && !["Super Admin", "Admin"].includes(selectedFunctionalRole)) {
        if (selectedTenants.length === 0) e.tenants = "Select at least one organization.";
        if (selectedTenants.some(t => t.location_ids.length === 0)) e.tenants = "Each organization must have at least one location.";
      }
      if (!isAdminRole && myTasksAccess) {
        if (!patientLimit || Number(patientLimit) < 1) {
          e.patientLimit = "Patient limit must be at least 1.";
        }
      }
    } else {
      if (!serviceType) e.serviceType = "Service type is required.";
      if (!selectedNonTocaFunctionalRole) e.functionalRole = "Functional role is required.";
      if (!selectedPosition) e.position = "Position is required.";
      if (selectedNonTocaLocations.length === 0) e.locations = "Select at least one location.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData, accountType, selectedFunctionalRole, selectedTenants, serviceType, selectedNonTocaFunctionalRole, selectedPosition, selectedNonTocaLocations, patientLimit, myTasksAccess]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const base = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      username: formData.email,
      password: formData.password,
    };
    if (accountType === "toca") {
      const payload = {
        ...base,
        user_type: "TOCA",
        functional_role: selectedFunctionalRole,
        chatbot_access: chatbotAccess,
        my_tasks_access: myTasksAccess,
        account_roles: [2],
        accessible_tenants: isAdminRole ? allTenantsPayload : selectedTenants,
      };
      payload.max_patient_limit = isNurseOrCareManager ? Number(patientLimit) : 0;
      addStaff(payload);
    } else {
      addStaff({
        ...base,
        user_type: "NON_TOCA",
        service_type: serviceType,
        functional_role: selectedNonTocaFunctionalRole,
        chatbot_access: chatbotAccess,
        my_tasks_access: myTasksAccess,
        account_position: Number(selectedPosition),
        max_patient_limit: isNurseOrCareManager ? Number(patientLimit) : 0,
        locations: selectedNonTocaLocations,
      });
    }
  };

  const isFormValid = () => {
    if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.email?.trim() || !formData.password?.trim() || !formData.phone?.trim()) return false;
    if (accountType === "toca") {
      if (!selectedFunctionalRole) return false;
      if (!["Super Admin", "Admin"].includes(selectedFunctionalRole) && selectedTenants.length === 0) return false;
      if (!isAdminRole && myTasksAccess && (!patientLimit || Number(patientLimit) < 1)) return false;
    } else {
      if (!serviceType || !selectedNonTocaFunctionalRole || !selectedPosition) return false;
      if (selectedNonTocaLocations.length === 0) return false;
    }
    return true;
  };

  // ─── Derived values ───────────────────────────────────────────────────────────

  const selectedPositionName = getPositionName(selectedPosition, dropdownData?.positions);
  const currentRoleConfig = accountType === "toca"
    ? getRoleConfig(selectedFunctionalRole, "toca", "", "")
    : getRoleConfig(selectedNonTocaFunctionalRole, "non_toca", serviceType, selectedPositionName);
  const isAdminRole = accountType === "toca" && ["Super Admin", "Admin"].includes(selectedFunctionalRole);
  // Patient limit hidden ONLY for Super Admin — all other toca roles with my_tasks access show it
  const isSuperAdmin = accountType === "toca" && selectedFunctionalRole === "Super Admin";
  const allTenantsPayload = (dropdownData?.tenants || []).map(tenant => ({
    tenant_id: tenant.tenant_id,
    location_ids: tenant.locations?.map(l => l.id) || [],
  }));
  const isChatbotEditable = currentRoleConfig?.copilot?.editable && apiChatbotAccess;
  const isMyTasksEditable = currentRoleConfig?.my_tasks?.editable && apiMyTasksAccess ;
 

  if (!isOpen) return null;

  const isCareManager =
    (accountType === "toca" && selectedFunctionalRole === "Care Manager") ||
    (
      accountType === "non_toca" &&
      selectedNonTocaFunctionalRole === "Care Manager" &&
      selectedPositionName === "Medical Assistant"
    );

  const isNurse =
    (accountType === "toca" && selectedFunctionalRole === "Nurse") ||
    (
      accountType === "non_toca" &&
      selectedPositionName === "Nurse"
    );
  
  const isNurseOrCareManager = isCareManager || isNurse

  return (
    <Dialog open={isOpen} setOpen={handleClose} className={styles.modal_content}>
      <DialogContent className={styles.modal_content}>
        <DialogHeader>
          <DialogTitle>Add Staff Member</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {modalLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner />
              <span className={styles.loadingText}>Loading form...</span>
            </div>
          ) : (
            <form className={`${styles.comp_wrapper} ${loading ? styles.loading : ""}`} onSubmit={handleSubmit}>

              {/* ── Basic Information ── */}
              <div className={styles.formection}>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label>First Name</label>
                    <input type="text" name="first_name" placeholder="First Name" value={formData.first_name} onChange={handleChange} className={errors.first_name ? styles.inputError : ""} />
                    {errors.first_name && <span className={styles.errorText}>{errors.first_name}</span>}
                  </div>
                  <div className={styles.input_field}>
                    <label>Last Name</label>
                    <input type="text" name="last_name" placeholder="Last Name" value={formData.last_name} onChange={handleChange} className={errors.last_name ? styles.inputError : ""} />
                    {errors.last_name && <span className={styles.errorText}>{errors.last_name}</span>}
                  </div>
                </div>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label>Email</label>
                    <input type="email" name="email" placeholder="email@example.com" value={formData.email} onChange={handleChange} className={errors.email ? styles.inputError : ""} />
                    {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                  </div>
                  <div className={styles.input_field}>
                    <label>Password</label>
                    <input type="password" name="password" placeholder="••••••••" value={formData.password} onChange={handleChange} className={errors.password ? styles.inputError : ""} />
                    {errors.password && <span className={styles.errorText}>{errors.password}</span>}
                  </div>
                </div>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label>Phone</label>
                    <Input
                      className="no-arrows"
                      type="text"
                      mask={true}
                      maskPattern="(999) 999-9999"
                      name="phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      inputClassName={errors.phone ? styles.inputError : ""}
                    />
                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                  </div>
                  <div className={styles.input_field}>
                    <label>User Type</label>
                    <div className={styles.toggleGroup}>
                      {/* ── Mutually exclusive: turning one ON turns the other OFF ── */}
                      {isTocaUser && (
                        <Toggle
                          label="TOCA"
                          checked={accountType === "toca"}
                          onChange={() => handleAccountTypeChange("toca")}
                        />)}
                      <Toggle
                        label="Non-TOCA"
                        checked={accountType === "non_toca"}
                        onChange={() => handleAccountTypeChange("non_toca")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Role & Access ── */}
              <div className={styles.formection}>

                {/* TOCA */}
                {accountType === "toca" && dropdownData && (
                  <>
                    <div className={styles.input_group}>
                      <div className={`${styles.input_field} ${styles.fullWidth}`}>
                        <label>Role</label>
                        <Select value={selectedFunctionalRole} onChange={handleTocaRoleChange}>
                          <SelectTrigger className={errors.functionalRole ? styles.inputError : ""}>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {(dropdownData.functional_roles || []).map((role) => {
                              const roleValue = extractRoleValue(role);
                              const roleLabel = extractRoleLabel(role);
                              return <SelectItem key={roleValue} value={roleValue}>{roleLabel}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                        {errors.functionalRole && <span className={styles.errorText}>{errors.functionalRole}</span>}
                      </div>
                      {/* Patient Limit — only show when Care Manager or Nurse is selected */}
                      {isNurseOrCareManager ? (
                        <div className={`${styles.input_field} ${styles.fullWidth}`}>
                          <label>Max Patient Limit</label>
                          <input
                            type="number"
                            min="1"
                            max="999999"
                            maxLength={3}
                            placeholder="Enter max patient limit (up to 3 digits)"
                            value={patientLimit}
                            onKeyDown={e => {
                              // Block if already 3 digits and not a control key
                              if (
                                String(patientLimit).length >= 3 &&
                                !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                            onChange={e => {
                              const val = e.target.value;
                              // Hard cap at 3 digits
                              if (val === "" || (Number(val) >= 1 && val.length <= 3)) {
                                setPatientLimit(val);
                                setErrors(p => ({ ...p, patientLimit: "" }));
                              }
                            }}
                            className={`no-arrows ${errors.patientLimit ? styles.inputError : ""}`}
                          />
                          {errors.patientLimit && (
                            <span className={styles.errorText}>{errors.patientLimit}</span>
                          )}
                        </div>
                        // </div>
                      ) : (
                        <div className={`${styles.input_field} ${styles.fullWidth}`}>
                        </div>
                      )}
                    </div>

                    {selectedFunctionalRole && (
                      <div className={styles.accessControlSection}>
                        <label className={styles.accessControlLabel}>Access Controls</label>
                        <div className={styles.checkboxGroup}>
                          <label className={`${styles.checkboxLabel} ${!isChatbotEditable ? styles.checkboxDisabled : ""}`}>
                            <input
                              type="checkbox"
                              checked={chatbotAccess}
                              disabled={!isChatbotEditable}
                              onChange={e => setChatbotAccess(e.target.checked)}
                            />
                            <span>Chatbot Access</span>
                          </label>
                          <label className={`${styles.checkboxLabel} ${!isMyTasksEditable ? styles.checkboxDisabled : ""}`}>
                            <input
                              type="checkbox"
                              checked={myTasksAccess}
                              disabled={!isMyTasksEditable}
                              onChange={e => {
                                setMyTasksAccess(e.target.checked);
                                // Clear patient limit if my tasks is turned off
                                if (!e.target.checked) setPatientLimit("");
                              }}
                            />
                            <span>My Tasks Access</span>
                          </label>
                        </div>
                      </div>
                    )}


                  </>
                )}

                {/* NON-TOCA */}
                {accountType === "non_toca" && dropdownData && (
                  <>
                    {/* Step 1: Service Type */}
                    <div className={styles.input_group}>
                      <div className={`${styles.input_field} ${styles.fullWidth}`}>
                        <label>Service Type</label>
                        <Select value={serviceType} onChange={handleServiceTypeChange}>
                          <SelectTrigger className={errors.serviceType ? styles.inputError : ""}>
                            <SelectValue placeholder="Select service type" />
                          </SelectTrigger>
                          <SelectContent>
                            {(dropdownData.service_types || []).map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.serviceType && <span className={styles.errorText}>{errors.serviceType}</span>}
                      </div>

                      {/* Step 2: Functional Role — always visible after service type is set */}
                      {serviceType && (
                        <div className={`${styles.input_field} ${styles.fullWidth}`}>
                          <label>Functional Role</label>
                          <Select value={selectedNonTocaFunctionalRole} onChange={handleNonTocaRoleChange}>
                            <SelectTrigger className={errors.functionalRole ? styles.inputError : ""}>
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                            <SelectContent>
                              {(dropdownData.functional_roles || []).map(r => {
                                const rValue = extractRoleValue(r);
                                const rLabel = extractRoleLabel(r);
                                return <SelectItem key={rValue} value={rValue}>{rLabel}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                          {errors.functionalRole && <span className={styles.errorText}>{errors.functionalRole}</span>}
                        </div>
                      )}
                    </div>

                    {/* Step 3: Position — filtered by role, visible after role selected */}
                    {selectedNonTocaFunctionalRole && (
                      <div className={styles.input_group}>
                        <div className={`${styles.input_field} ${styles.fullWidth}`}>
                          <label>Position</label>
                          <Select
                            value={selectedPositionName}
                            onChange={(positionName) => {
                              const matched = (dropdownData.positions || []).find(p => p.name === positionName);
                              if (matched) handlePositionChange(matched.id);
                            }}
                          >
                            <SelectTrigger className={errors.position ? styles.inputError : ""}>
                              <SelectValue placeholder="Select position" />
                            </SelectTrigger>
                            <SelectContent>
                              {(dropdownData.positions || [])
                                .filter(p => (ROLE_POSITION_MAP[selectedNonTocaFunctionalRole] || []).includes(p.name))
                                .map(p => (
                                  <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                          {errors.position && <span className={styles.errorText}>{errors.position}</span>}
                        </div>

                        {/* Patient Limit — only show when Care Manager or Nurse is selected */}
                        {isNurseOrCareManager ? (
                          <div className={`${styles.input_field} ${styles.fullWidth}`}>
                            <label>Max Patient Limit</label>
                            <input
                              type="number"
                              min="1"
                              max="999999"
                              maxLength={3}
                              placeholder="Enter max patient limit (up to 3 digits)"
                              value={patientLimit}
                              onKeyDown={e => {
                                // Block if already 3 digits and not a control key
                                if (
                                  String(patientLimit).length >= 3 &&
                                  !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={e => {
                                const val = e.target.value;
                                // Hard cap at 3 digits
                                if (val === "" || (Number(val) >= 1 && val.length <= 3)) {
                                  setPatientLimit(val);
                                  setErrors(p => ({ ...p, patientLimit: "" }));
                                }
                              }}
                              className={`no-arrows ${errors.patientLimit ? styles.inputError : ""}`}
                            />
                            {errors.patientLimit && (
                              <span className={styles.errorText}>{errors.patientLimit}</span>
                            )}
                          </div>
                        ) : (
                          <div className={`${styles.input_field} ${styles.fullWidth}`}>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 4: Access Control — visible only when both role AND position selected */}
                    {selectedNonTocaFunctionalRole && selectedPosition && (
                      <div className={styles.accessControlSection}>
                        <label className={styles.accessControlLabel}>Access Control</label>
                        <div className={styles.checkboxGroup}>
                          <label className={`${styles.checkboxLabel} ${!isChatbotEditable ? styles.checkboxDisabled : ""}`}>
                            <input
                              type="checkbox"
                              checked={chatbotAccess}
                              disabled={!isChatbotEditable}
                              onChange={e => setChatbotAccess(e.target.checked)}
                            />
                            <span>Chatbot Access</span>
                          </label>
                          {/* <label className={`${styles.checkboxLabel} ${!isMyTasksEditable ? styles.checkboxDisabled : ""}`}> */}
                          <label className={`${styles.checkboxLabel} ${!isMyTasksEditable ? styles.checkboxDisabled : ""}`}>
                            <input
                              type="checkbox"
                              checked={myTasksAccess}
                              disabled={!isMyTasksEditable}
                              // disabled={!isNurseOrCareManager}
                              onChange={e => setMyTasksAccess(e.target.checked)}
                            />
                            <span>My Tasks Access</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ── Account Access (TOCA) ── */}
              {accountType === "toca" && selectedFunctionalRole && !isAdminRole && (
                <div className={styles.formSection}>
                  <h4 className={styles.sectionTitle}>Organizational Access</h4>
                  <div className={styles.orgLocationSection}>
                    <p className={styles.sectionDescription}>Select organizations and locations for this user</p>
                    {(dropdownData.tenants || []).length === 0 ? (
                      <div className={styles.emptyState}>No organizations available</div>
                    ) : (
                      <div className={styles.tenantList}>
                        {(dropdownData.tenants || []).map(tenant => {
                          const isSelected = selectedTenants.some(t => t.tenant_id === tenant.tenant_id);
                          const selectedCount = selectedTenants.find(t => t.tenant_id === tenant.tenant_id)?.location_ids?.length || 0;
                          const isExpanded = expandedTenants.has(tenant.tenant_id);
                          const totalLocs = tenant.locations?.length || 0;
                          return (
                            <div key={tenant.tenant_id} className={styles.tenantItem}>
                              <div
                                className={`${styles.tenantHeader} ${isSelected ? styles.tenantSelected : ""}`}
                                onClick={() => setExpandedTenants(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(tenant.tenant_id)) {
                                    newSet.delete(tenant.tenant_id);
                                  } else {
                                    newSet.add(tenant.tenant_id);
                                  }
                                  return newSet;
                                })}
                              >
                                <div className={styles.tenantInfo}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={e => { e.stopPropagation(); handleTenantToggle(tenant); }}
                                    className={styles.tenantCheckbox}
                                    onClick={e => e.stopPropagation()}
                                  />
                                  <span className={styles.tenantName}>{tenant.tenant_name}</span>
                                </div>
                                <div className={styles.tenantMeta}>
                                  {isSelected && <span className={styles.locationCount}>{selectedCount}/{totalLocs} locations</span>}
                                  <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ""}`}>
                                    {isExpanded ? "▼" : "▶"}
                                  </span>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className={styles.locationsList}>
                                  {tenant.locations?.map(loc => {
                                    const tenantEntry = selectedTenants.find(t => t.tenant_id === tenant.tenant_id);
                                    const isChecked = tenantEntry?.location_ids?.includes(loc.id);
                                    const isLastLocation = tenantEntry?.location_ids?.length === 1 && isChecked;
                                    return (
                                      <label
                                        key={loc.id}
                                        className={`${styles.locationItem} ${isChecked ? styles.locationSelected : ""} ${isLastLocation ? styles.locationDisabled : ""}`}
                                        title={isLastLocation ? "At least one location must be selected" : ""}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={!!isChecked}
                                          disabled={isLastLocation}
                                          onChange={() => handleLocationToggle(tenant.tenant_id, loc.id)}
                                        />
                                        <span>{loc.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {errors.tenants && <span className={styles.errorText}>{errors.tenants}</span>}
                  </div>
                </div>
              )}

              {/* ── Account Access (NON-TOCA) ── */}
              {accountType === "non_toca" && selectedNonTocaFunctionalRole && (
                <div className={styles.formSection}>
                  <h4 className={styles.sectionTitle}>Account Access</h4>
                  <div className={styles.assign_item}>
                    <div className={styles.items}>
                      {(dropdownData.locations || []).length === 0 ? (
                        <div className={styles.emptyState}>No locations available</div>
                      ) : (
                        (dropdownData.locations || []).map(loc => {
                          const isChecked = selectedNonTocaLocations.includes(loc.id);
                          const isLastLocation = selectedNonTocaLocations.length === 1 && isChecked;
                          return (
                            <label
                              key={loc.id}
                              className={`${styles.locationLabel} ${isLastLocation ? styles.locationDisabled : ""}`}
                              title={isLastLocation ? "At least one location must be selected" : ""}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isLastLocation}
                                onChange={() => handleNonTocaLocationToggle(loc.id)}
                              />
                              {loc.name}
                            </label>
                          );
                        })
                      )}
                    </div>
                    {errors.locations && <span className={styles.errorText}>{errors.locations}</span>}
                  </div>
                </div>
              )}

            </form>
          )}
        </DialogBody>
        <DialogFooter className={styles.modalFooter}>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={loading} disabled={!isFormValid()}>Add Staff</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export const EditStaffModal = ({
  isOpen,
  onClose,
  staff_info,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    first_name: staff_info.user.first_name,
    last_name: staff_info.user.last_name,
    email: staff_info.user.email,
    // account_position: staff_info.account_position.id,
    primary_phone: staff_info.user.primary_phone,
  });

  const [selectedRoles, setSelectedRoles] = useState({
    account_role: "",
    inventory_role: "",
  });

  const [checkedLocations, setCheckedLocations] = useState({});
  const [inventoryRoleVisibility, setInventoryRoleVisibility] = useState(false);

  // SCRUM-992: this staff member's default account / location / landing page.
  // Hidden if the admin can't load them (e.g. not Super User / Account Admin).
  const defaults = useDefaultPreferences({
    staffId: staff_info?.id,
    enabled: isOpen && !!staff_info?.id,
  });

  const InventoryRoles = ["Inventory Manager", "TOCA Inventory Manager"];
  const AccountRoles = ["Account Admin", "Super User", "TOCA Admin"];

  function groupRoles(roles) {
    return {
      inventory_role: roles.filter((_role) =>
        InventoryRoles.includes(_role.name)
      ),
      account_role: roles.filter((_role) => AccountRoles.includes(_role.name)),
    };
  }

  useEffect(() => {
    if (!staff_info || !roles.inventory_roles || !roles.other_roles) return;

    const defaultRoles = [...roles.inventory_roles, ...roles.other_roles];
    const findRole = (roleName) =>
      defaultRoles.find((r) => r.name === roleName);

    const staffRoles = [
      ...(staff_info.account_roles || []),
      ...(staff_info.external_role ? [staff_info.external_role] : []),
    ];

    let previousRoles = groupRoles(staffRoles);

    if (previousRoles.account_role?.some((r) => r.name === "TOCA Admin")) {
      const tocaInventoryManager = findRole("TOCA Inventory Manager");
      const tocaAdmin = findRole("TOCA Admin");
      if (tocaInventoryManager && tocaAdmin) {
        previousRoles.inventory_role = [tocaInventoryManager];
        previousRoles.account_role = [tocaAdmin];
      }
    }

    setSelectedRoles((prev) => ({
      ...prev,
      account_role: previousRoles.account_role?.[0]?.name || prev.account_role,
      inventory_role:
        previousRoles.inventory_role?.[0]?.name || prev.inventory_role,
    }));
  }, [staff_info, roles]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");

    const fetchLocations = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/locations/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.locations.filter((location) => location.active);
    };

    const fetchRoles = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/roles/?staff_roles=true&external_roles=true`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchPositions = async () => {
      const url = `${BASE_URL}/accounts/${account_id}/positions/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    };

    const fetchAllData = async () => {
      try {
        setModalLoading(true); // Set loading before fetching data

        const [locationsData, rolesData, positionsData] = await Promise.all([
          fetchLocations(),
          fetchRoles(),
          fetchPositions(),
        ]);

        setRoles({
          inventory_roles: rolesData.inventory_roles,
          other_roles: rolesData.other_roles.filter(
            (_role) => _role.name !== "TOCA Admin"
          ),
        });
        setLocations(locationsData);
        setPositions(positionsData);
      } catch (error) {
        handleApiError(error);
      } finally {
        setModalLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    if (staff_info?.locations) {
      const initialCheckedLocations = staff_info.locations.reduce(
        (acc, location) => {
          acc[location.id] = true;
          return acc;
        },
        {}
      );

      setCheckedLocations(initialCheckedLocations);
    }
  }, [staff_info]);

  useEffect(() => {
    if (
      selectedRoles?.account_role &&
      selectedRoles.account_role !== "Account Admin"
    ) {
      setCheckedLocations({});
    }
  }, [selectedRoles.account_role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleValue = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const editStaff = async (finalFormData) => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/staff/${staff_info.id}/edit/`;

    setLoading(true);
    // Defaults first, so a validation error keeps the modal open with the
    // message under the field instead of closing after the staff save.
    try {
      await defaults.save();
    } catch (error) {
      setLoading(false);
      handleApiError(error);
      return;
    }

    try {
      const response = await axios.patch(url, finalFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLoading(false);
      comp_renderer((prev) => !prev);
      onClose();
      toast.success("Staff Edited successfully.");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const validateForm = (finalFormData) => {
    let errors = [];

    if (!finalFormData.first_name?.trim())
      errors.push("First Name is required.");
    if (!finalFormData.email?.trim()) errors.push("Email is required.");
    // if (!finalFormData.password?.trim()) {
    //   errors.push("Password is required.");
    // }

    // If account role is Account Admin (string), require at least one location
    if (
      selectedRoles.account_role === "Account Admin" &&
      (!finalFormData.locations || finalFormData.locations.length === 0)
    ) {
      errors.push("Please select at least one location.");
    }

    if (errors.length > 0) {
      toast.error(errors.join("\n"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line",
          textAlign: "left",
        },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedLocationIds = Object.keys(checkedLocations)
      .filter((key) => checkedLocations[key] === true)
      .map((key) => Number(key));

    const defaultRoles = [...roles.inventory_roles, ...roles.other_roles];

    const findRole = (name) => defaultRoles.find((role) => role.name === name);

    const selectedNames = [
      selectedRoles.account_role,
      selectedRoles.inventory_role,
    ];

    const selectedRolesList = selectedNames
      .map(findRole)
      .filter((role) => role !== undefined);

    const rolesByType = {};
    selectedRolesList.forEach((role) => {
      if (!rolesByType[role.type]) {
        rolesByType[role.type] = [];
      }
      rolesByType[role.type].push(role);
    });

    const externalRoles = rolesByType.external_role || [];
    const isTocaAdmin = externalRoles.some(
      (role) => role.name === "TOCA Admin"
    );
    if (isTocaAdmin) {
      const superUser = findRole("Super User");
      rolesByType.account_role = [superUser];
      rolesByType.external_role = externalRoles.filter(
        (role) => role.name === "TOCA Admin"
      );
    }

    const accountIds = (rolesByType.account_role || []).map((role) => role.id);
    const externalIds = (rolesByType.external_role || []).map(
      (role) => role.id
    );

    const hasSuperUser =
      rolesByType?.account_role?.some((role) => role?.name === "Super User") &&
      !rolesByType?.external_role?.some((role) => role?.name === "TOCA Admin");

    let finalFormData = {
      ...formData,
      locations: selectedLocationIds,
      account_roles: accountIds,
      external_role: externalIds[0] ? externalIds[0] : null,
    };

    if (validateForm(finalFormData)) {
      editStaff(finalFormData);
    }
  };

  const handleLocationCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setCheckedLocations((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const isInventorySelectDisabled =
    (selectedRoles.account_role === "Account Admin" ||
      selectedRoles.account_role === "TOCA Admin") &&
    selectedRoles.external_role !== "";

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Staff</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {modalLoading ? (
            <Spinner />
          ) : (
            <form
              className={`${styles.comp_wrapper} ${loading ? styles.loading : ""
                }`}
            >
              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First Name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.input_field}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last Name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.input_field}>
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Phone</label>
                  <Input
                    className="no-arrows"
                    type="text"
                    mask={true}
                    maskPattern="(999) 999-9999"
                    name="primary_phone"
                    placeholder="Phone"
                    value={formData.primary_phone}
                    onChange={handleChange}
                  />
                </div>

                {/* <div className={styles.input_field}>
                  <label>Position</label>

                  <Select
                    onChange={(value) => handleValue("account_position", value)}
                    value={formData.account_position}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a position">
                        <p>
                          {positions.find(
                            (_pos) => _pos.id == formData.account_position
                          )?.name || "Select a position"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="default value" value="">
                        -- None --
                      </SelectItem>
                      {positions &&
                        positions.map((position) => (
                          <SelectItem key={position.id} value={position.id}>
                            {position.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div> */}
              </div>

              {/* <div className={`${styles.input_group} `}>
                <div className={styles.input_field}>
                  <label>Role</label>
                  <Select
                    onChange={(value) => {
                      let newInventoryRole = selectedRoles.inventory_role; // Preserve current unless changing
                      let newAccountRole = value || null;

                      if (value === "Account Admin") {
                        newInventoryRole = "Inventory Manager";
                      } else if (value === "Super User") {
                        newInventoryRole = "";
                      } else if (value === "TOCA Admin") {
                        newInventoryRole = "TOCA Inventory Manager";
                      } else {
                        newInventoryRole = "";
                        newAccountRole = ""; // Or handle as needed
                      }

                      // Single setFormData call for all changes
                      setSelectedRoles((prev) => ({
                        ...prev,
                        account_role: newAccountRole,
                        inventory_role: newInventoryRole,
                      }));
                    }}
                    value={selectedRoles.account_role}
                  // ... existing code ...other_roles
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role">
                        <p>
                          {roles?.other_roles?.find(
                            (_pos) => _pos.name == selectedRoles.account_role
                          )?.name || "Select a role"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="default value" value="">
                        -- None --
                      </SelectItem>
                      {roles &&
                        roles?.other_roles?.map((role) => (
                          <SelectItem key={role.name} value={role.name} la>
                            <p title={role.description}>{role.name}</p>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={styles.input_field}>
                  <label>Inventory Role</label>
                  <Select
                    key={selectedRoles.inventory_role}
                    disabled={isInventorySelectDisabled}
                    onChange={(value) =>
                      setSelectedRoles((prev) => ({
                        ...prev,
                        inventory_role: value || null,
                      }))
                    }
                    value={selectedRoles.inventory_role}
                  // disabled={!formData.account_role}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role">
                        <p>
                          {roles?.inventory_roles?.find(
                            (_pos) => _pos.name === selectedRoles.inventory_role
                          )?.name || "Select a role"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="default value" value="">
                        -- None --
                      </SelectItem>
                      {roles &&
                        roles?.inventory_roles
                          ?.filter(
                            (_role) =>
                              (selectedRoles.account_role === "Super User" ||
                                selectedRoles.account_role === "") &&
                              _role.name === "TOCA Inventory Manager"
                          )

                          ?.map((role) => (
                            <SelectItem key={role.name} value={role.name}>
                              <p title={role.description}>{role.name}</p>
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>
              </div> */}

              {/* {selectedRoles.account_role === "Account Admin" && (
                <div className={styles.assign_item}>
                  <h3>Assign Locations</h3>
                  <div className={styles.items}>
                    {locations.map((location) => (
                      <label key={location.id}>
                        <input
                          type="checkbox"
                          name={location.id}
                          checked={checkedLocations[location.id] || false}
                          onChange={handleLocationCheckBoxChange}
                        />
                        {location.name}
                      </label>
                    ))}
                  </div>
                </div>
              )} */}

              <DefaultPreferencesFields
                defaults={defaults}
                rowClassName={styles.input_group}
                fieldClassName={styles.input_field}
              />
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteStaff = ({
  isOpen,
  onClose,
  staff_info,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const inactiveStaff = async () => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/staff/${staff_info.id}/edit/`;

    try {
      setLoading(true);
      const response = await axios.patch(
        url,
        { active: false },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      comp_renderer((prev) => !prev);

      onClose();
      toast.success("Staff Deleted successfully.");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    inactiveStaff();
  };

  if (!isOpen) return null;

  return (
    <AlertDialog
      open={isOpen}
      setOpen={onClose}
      title="Delete Staff"
      description={`Are you sure you want to remove ${staff_info?.user?.first_name} ${staff_info?.user?.last_name}`}
    >
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button
        loading={loading}
        variant="danger"
        size="default"
        onClick={handleSubmit}
      >
        Delete
      </Button>
    </AlertDialog>
  );
};

export const ActiveStaffModal = ({
  isOpen,
  onClose,
  staff_info,
  comp_renderer,
  current_render,
}) => {
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  // const handleCheckBoxChange = (e) => {
  //     const { name, checked } = e.target;
  //     setFormData((prev) => ({ ...prev, [name]: checked }));
  // };

  const activeStaff = async () => {
    const token = localStorage.getItem("authToken");
    const account_id = sessionStorage.getItem("acc_id");
    const url = `${BASE_URL}/accounts/${account_id}/staff/${staff_info.id}/edit/`;

    try {
      setLoading(true);
      const response = await axios.patch(
        url,
        { active: true },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      comp_renderer((prev) => !prev);
      onClose();
      toast.success("Staff Activated successfully.");
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    activeStaff();
  };

  if (!isOpen) return null;

  return (
    <AlertDialog
      open={isOpen}
      setOpen={onClose}
      title="Activate Staff"
      description={`Are you sure you want to activate ${staff_info.user.first_name} ${staff_info.user.last_name}`}
    >
      <Button variant="normal" size="default" onClick={onClose}>
        Cancel
      </Button>
      <Button loading={loading} onClick={handleSubmit}>
        Acitvate
      </Button>
    </AlertDialog>
  );
};